<template>
  <div class="player-picker">
    <div class="form-row">
      <div class="form-group name-field">
        <label>Nom *</label>
        <input :value="name" class="form-input" placeholder="NOM" autocomplete="off"
          @input="onNameInput(($event.target as HTMLInputElement).value)" @focus="open = true"
          @blur="onBlur" />
        <ul v-if="open && suggestions.length" class="suggestions">
          <li v-for="(s, i) in suggestions" class="suggestion-row" @mousedown.prevent="pick(s)"
            :key="`${s.name}-${s.firstname}-${s.club_name ?? ''}-${i}`">
            <span class="suggestion-name">{{ s.firstname }} {{ s.name }}</span>
            <span class="suggestion-meta">
              <span v-if="s.club_name" class="suggestion-club">{{ s.club_name }}</span>
              <span class="suggestion-cat">{{ s.category }}</span>
            </span>
          </li>
        </ul>
      </div>
      <div class="form-group">
        <label>Prénom *</label>
        <input :value="firstname" class="form-input" placeholder="Prénom" autocomplete="off"
          @input="$emit('update:firstname', formatFirstname(($event.target as HTMLInputElement).value))" />
      </div>
    </div>
    <p v-if="hintVisible" class="picker-hint">
      {{ remoteHint ?? (remote ? 'Tapez pour rechercher un licencié (toute la fédération), ou saisissez librement un autre joueur.'
        : 'Tapez pour choisir un licencié du club, ou saisissez librement un autre joueur.') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { formatFirstname } from '@/functions/utils'

export interface RosterEntry {
  name: string
  firstname: string
  category: string
  gender?: string
  // Absents des résultats du formulaire d'inscription public (cf. Dartshub
  // events/routes.py::registration_license_search), qui ne les expose pas.
  birth_date?: string | null
  license_number?: number
  club_name?: string
}

const props = defineProps<{
  name: string
  firstname: string
  roster?: RosterEntry[]
  remote?: boolean
  remoteResults?: RosterEntry[]
  // Remplace le texte d'aide par défaut (ex. périmètre de recherche restreint au comité).
  remoteHint?: string
  licensePick?: RosterEntry | null
}>()

const emit = defineEmits<{
  (e: 'update:name', val: string): void
  (e: 'update:firstname', val: string): void
  (e: 'pick', license: RosterEntry): void
  (e: 'search', query: string): void
}>()

const open = ref(false)

const normalize = (s: string) => s.toLowerCase().trim()

const suggestions = computed<RosterEntry[]>(() => {
  const q = normalize(props.name)
  if (q.length < 2) return []
  const pool = props.remote ? (props.remoteResults ?? []) : (props.roster ?? [])
  // Le nom (roster local, ou dernière recherche fédérale déclenchée par le champ nom)
  // ne suffit pas seul : sans reprendre aussi le prénom déjà saisi, un homonyme dont le
  // prénom ne correspond plus resterait affiché comme suggestion après une saisie
  // corrective du prénom, alors qu'il ne s'agit plus de la même personne.
  // Mais un prénom déjà rempli (prérempli, ou resté d'un autre joueur) ne doit pas tout
  // masquer : sans aucune correspondance sur le prénom, on revient aux seuls résultats
  // sur le nom.
  const qFirst = normalize(props.firstname)
  const byName = pool.filter(r =>
    normalize(`${r.firstname} ${r.name}`).includes(q) || normalize(`${r.name} ${r.firstname}`).includes(q)
  )
  const byBoth = qFirst ? byName.filter(r => normalize(r.firstname).includes(qFirst)) : byName
  return (byBoth.length ? byBoth : byName).slice(0, 8)
})

const hintVisible = computed(() =>
  !props.licensePick && ((props.roster && props.roster.length) || props.remote)
)

let searchTimer: ReturnType<typeof setTimeout> | undefined

const onNameInput = (val: string) => {
  const upper = val.toUpperCase()
  emit('update:name', upper)
  if (props.remote) {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => emit('search', upper), 350)
  }
}

const onBlur = () => {
  setTimeout(() => { open.value = false }, 150)
}

const pick = (entry: RosterEntry) => {
  emit('update:name', entry.name)
  emit('update:firstname', entry.firstname)
  emit('pick', entry)
  open.value = false
}
</script>

<style scoped>
.player-picker {
  position: relative;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  margin-bottom: 0.4rem;
  position: relative;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: var(--color-fg, #333);
}

.form-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-light-shadow, #ccc);
  border-radius: var(--radius, 6px);
  font-size: 0.95rem;
  background: var(--color-bg, #fff);
  color: var(--color-fg, #333);
  box-sizing: border-box;
}

.suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  margin: 2px 0 0;
  padding: 0.25rem;
  list-style: none;
  background: var(--color-bg-lighter, #fff);
  border: 1px solid var(--color-light-shadow, #ccc);
  border-radius: var(--radius, 6px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  max-height: 220px;
  overflow-y: auto;
}

.suggestion-row {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.88rem;
  color: var(--color-fg, #333);
}

.suggestion-row:hover {
  background: color-mix(in srgb, var(--color-accent, #1a3a5c) 15%, var(--color-bg-lighter, #fff));
}

.suggestion-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.suggestion-club {
  color: var(--color-fg-darker, #888);
  font-size: 0.78rem;
}

.suggestion-cat {
  color: var(--color-fg-darker, #888);
  font-size: 0.78rem;
  font-weight: 600;
}

.picker-hint {
  margin: 0 0 0.6rem;
  font-size: 0.78rem;
  color: var(--color-fg-darker, #888);
}

@media (max-width: 600px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
